import React, { createContext, useCallback, useContext, useState } from "react";
import { ConfirmationDialogRaw } from "components/commons/dialog/confirmDialog/confirmDialog";

// Kiểu cho options khi gọi confirm
export type ConfirmOptions = {
  title: string;
  content: React.ReactNode | string;
  value?: "info" | "warn" | "error" | "delete";
  labelOk?: string;
  labelCancel?: string;
  component?: React.ReactNode;
};

// Kiểu context
type ConfirmContextType = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

// Tạo context
const ConfirmContext = createContext<ConfirmContextType | null>(null);

// Hook tiện lợi để dùng confirm()
export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx.confirm;
};

// Provider bọc toàn bộ app
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null);

  // Hàm confirm trả về Promise<boolean>
  const confirm = useCallback((opts: ConfirmOptions) => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve);
    });
  }, []);

  // Đóng dialog và resolve kết quả
  const handleClose = (isOk?: boolean) => {
    setOpen(false);
    resolver?.(!!isOk);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {options && (
        <ConfirmationDialogRaw
          id="global-confirm"
          keepMounted
          open={open}
          onClose={handleClose}
          {...options}
        />
      )}
    </ConfirmContext.Provider>
  );
};
